import React, { lazy, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AASTEPS } from '..';
import { AppContext } from '../../../App';
import { FlowType, isLogEnabled } from '../../../Utils/constants';
import {
  EVENTCODES,
  pushEventToParent,
  pushEventToPosthog,
} from '../../../Utils/eventUtils';
import {
  markIframeSuccess,
  markPostmessageReceived,
} from '../../../Utils/iframeTelemetry';
import { ROUTES } from '../../../Utils/routes';
import { getIsNewUser, getTenant } from '../../../Utils/storageUtils';
import {
  AccountDiscoveryStatus,
  LOADERTYPE,
  TenantType,
} from '../../../Utils/types';
import { isCommonTenant } from '../../CommonTenant/utils/configUtils';
import { VIEWTYPE } from '../../HomePage';

// Re-export for backward compatibility
export { AccountDiscoveryStatus };

export const loadPBAA = () => import('../../PaisaBazaar/AA');
export const loadCommonTenantAA = () => import('../../CommonTenant/AA');
export const loadYBAA = () => import('../../YesBank/AA');
export const loadJFSAA = () => import('../../JFS/AA');
export const loadAUAA = () => import('../../AUBank/AA');
export const loadSIBAA = () => import('../../SIB/AA');

const CommonTenantAA = lazy(loadCommonTenantAA);
const PBAA = lazy(loadPBAA);
const YesBankAA = lazy(loadYBAA);
const JFSAA = lazy(loadJFSAA);
const AUAA = lazy(loadAUAA);
const SIBAA = lazy(loadSIBAA);

const MahindraAA = lazy(() => {
  return import('../../Mahindra/AA');
});

interface Props {
  gotoRoot: any;
  goBackOnJourneyFailure: any;
  setAAInternalStep: any;
  isInfoAvailable: any;
  setIsInfoAvailable: any;
  redirectionURL: any;
  setPartialLoading: any;
  isPartial: any;
  partialLoading: any;
  sibNotesAcknowledged?: boolean;
  onAcknowledgeSibNotes?: () => void;
}

const AAWrapper: React.FC<Props> = ({
  setIsInfoAvailable,
  gotoRoot,
  goBackOnJourneyFailure,
  setAAInternalStep,
  isInfoAvailable,
  redirectionURL,
  isPartial,
  partialLoading,
  setPartialLoading,
  sibNotesAcknowledged,
  onAcknowledgeSibNotes,
}) => {
  const {
    isAdditionalAccountJourney,
    setIsLoading,
    isLoanJourney,
    setLoaderType,
    setAnalyticsStatus,
    setIsAccountDetailsFetched,
    setCurrentRoute,
    setSelectedFIP,
    statusData,
    setViewType,
    backEventSent,
    currentFlowType,
    setIsHeaderVisible,
    setIsBottomNavbarVisible,
    setBackButtonHandler,
    setShowToastAA,
    setShowAUErrorSheet,
    setPreFetchedLoadingForAssets,
    setStatusPolling,
    nudgesEntryPending,
    deepLinkHandled,
  }: any = useContext(AppContext);

  let consentApprovalCount = 0;
  let aaSelectionUsingProfileDiscovery: boolean | null = null;
  const navigate = useNavigate();
  const isJourneyCompletedEventCame = useRef(false);
  const [aaAuthScreenLoaded, setAaAuthScreenLoaded] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [accountDiscoveryStatus, setAccountDiscoveryStatus] =
    useState<AccountDiscoveryStatus>(AccountDiscoveryStatus.Default);
  // Incremented on REJECT_CONSENT_BUTTON_CLICKED from the AA SDK so SIBAA
  // opens its existing exit-reason sheet, keeping the reject flow consistent
  // with the manual back-button exit flow.
  const [exitReasonSheetTrigger, setExitReasonSheetTrigger] = useState(0);

  const updatedStatusDefaultConsentCreation = () => {
    setAccountDiscoveryStatus(AccountDiscoveryStatus.Default);
    setIsInfoAvailable(true);
    setPartialLoading(true);
  };

  const enableLoaderAndRouteToDashboard = () => {
    setIsLoading(true);
    setAnalyticsStatus('PENDING');
    setIsAccountDetailsFetched(false);
    setLoaderType(LOADERTYPE.DATALOADER);
    setSelectedFIP([]);
    gotoRoot();

    if (getTenant() === TenantType.MAHINDRA) {
      setViewType(VIEWTYPE.BANKSUMMARYVIEW);
    }
  };

  const handleRoutingOnJourneyCompleted = () => {
    updatedStatusDefaultConsentCreation();

    if (
      !isJourneyCompletedEventCame.current &&
      !statusData.current.approvedConsentsAvailable
    ) {
      if (getTenant() === TenantType.JFS) {
        setCurrentRoute(ROUTES.JFS_WELCOME);
        return;
      }
      if(isCommonTenant()) {
        setCurrentRoute(ROUTES.DASHBOARD)
      }
    }

    if (
      statusData.current &&
      statusData.current.accountDetails &&
      statusData.current.accountDetails.length > 0
    ) {
      gotoRoot();
    } else if (
      !isJourneyCompletedEventCame.current &&
      getTenant() !== TenantType.JFS &&
      getTenant() !== TenantType.MAHINDRA
    ) {
      setAAInternalStep(AASTEPS.FIP_SELECTION);
    } else {
      enableLoaderAndRouteToDashboard();
    }
  };
  useEffect(() => {
    if (getTenant() === TenantType.JFS) {
      setBackButtonHandler(() => {
        return () => {
          let frame: any = document.getElementById('aa-wrapper-iframe');
          if (frame && frame.contentWindow) {
            frame.contentWindow.postMessage(
              { PARENT_COMMAND: 'BACK_BUTTON_CLICKED' },
              '*',
            );
          } else {
            gotoRoot();
          }
        };
      });
    }
  }, []);

  const handleSDKEvents = (event: any) => {
    const { data } = event;

    if (getTenant() === TenantType.YESBANK) {
      markPostmessageReceived(event.origin, data);
    }

    if (isLogEnabled) {
      console.log('Inside AA HomePage', event.data ? event.data : '');
    }

    if (
      event?.data?.eventCode === 'REJECT_CONSENT_BUTTON_CLICKED' &&
      getTenant() === TenantType.SIB
    ) {
      setExitReasonSheetTrigger((prev) => prev + 1);
    }

    try {
      if (typeof data === 'string') {
        const parsedData = JSON.parse(data);
        if (
          parsedData.action === 'handleIframeBackAction' &&
          getTenant() !== TenantType.AU
        ) {
          let frame: any = document.getElementById('aa-wrapper-iframe');
          if (frame && frame.contentWindow) {
            frame.contentWindow.postMessage(
              { PARENT_COMMAND: 'BACK_BUTTON_CLICKED' },
              '*',
            );
          }
        }
      }
    } catch (error) {
      // captureException(error);
    }
    if (event && event.data && (event.data.otp || event.data.OTP)) {
      const otp = event.data.otp || event.data.OTP;
      let meta = undefined;
      if (
        getTenant() === TenantType.JFS ||
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.AU
      ) {
        if (currentFlowType !== undefined && currentFlowType !== null) {
          meta = { fiType: FlowType[currentFlowType] };
        }
      }
      pushEventToPosthog({
        eventCode: EVENTCODES.RECEIVED_AUTO_READ_EVENT,
        meta: meta,
      });
      let frame: any = document.getElementById('aa-wrapper-iframe');
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ OTP: otp }, '*');
        frame.contentWindow.postMessage({ otp: otp }, '*');
      }
    } else if (
      event &&
      event.data &&
      event.data.action &&
      event.data.action === 'handleIframeBackAction'
    ) {
      let frame: any = document.getElementById('aa-wrapper-iframe');
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage(
          { PARENT_COMMAND: 'BACK_BUTTON_CLICKED' },
          '*',
        );
      }
    } else if (
      event.data.eventCode &&
      (event.data.eventCode === 'USER_LOGGEDIN' ||
        event.data.eventCode === 'USER_SIGNEDUP')
    ) {
      setIsInfoAvailable(false);
    }

    if (event.data.eventCode && event.data.eventCode !== 'JOURNEY_COMPLETED') {
      let meta = undefined;
      if (
        event.data.eventCode === 'CONSENT_APPROVED_SUCCESS' ||
        event.data.eventCode === 'CONSENT_APPROVED_FAILED'
      ) {
        meta = {
          consentHandle: event.data.consentHandle[consentApprovalCount],
        };
        consentApprovalCount++;
      }

      if (
        getTenant() === TenantType.JFS ||
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.AU
      ) {
        if (currentFlowType !== undefined && currentFlowType !== null) {
          meta = {
            ...meta,
            fiType: FlowType[currentFlowType],
          };
        }
      }
      if (getTenant() === TenantType.JFS && event.data.eventCode) {
        if (
          (event.data.eventCode === 'AA_SELECTION_PROFILE_DISCOVERY_FAILURE' ||
            event.data.eventCode ===
              'AA_SELECTION_PROFILE_DISCOVERY_SUCCESS') &&
          aaSelectionUsingProfileDiscovery === null
        ) {
          if (event.data.eventCode === 'AA_SELECTION_PROFILE_DISCOVERY_FAILURE')
            aaSelectionUsingProfileDiscovery = false;
          else aaSelectionUsingProfileDiscovery = true;
        }
        if (aaSelectionUsingProfileDiscovery !== null) {
          meta = {
            ...meta,
            AASelectionUsingProfileDiscovery: aaSelectionUsingProfileDiscovery,
          };
        }
        if (event.data.eventCode.includes('selected_AA')) {
          meta = {
            ...meta,
            selectedAA: event.data.eventCode,
          };
        }
      }
      pushEventToParent({
        eventCode: event.data.eventCode,
        errorMessage: event.data.errorMessage,
        errorStatus: event.data.errorStatus,
        meta: meta,
        isFromAA: true,
        fiType:
          currentFlowType !== undefined && currentFlowType !== null
            ? FlowType[currentFlowType]
            : undefined,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_${event.data.eventCode}`,
            }
          : {}),
      });
    } else if (
      event.data.eventCode &&
      event.data.eventCode === 'JOURNEY_COMPLETED' &&
      (isLoanJourney.current ||
        getTenant() === TenantType.JFS ||
        getTenant() === TenantType.YESBANK)
    ) {
      let meta = undefined;
      if (
        getTenant() === TenantType.JFS ||
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.AU
      ) {
        if (currentFlowType) {
          meta = {
            fiType: FlowType[currentFlowType],
          };
        }
      }
      pushEventToParent({
        eventCode: event.data.eventCode,
        errorMessage: event.data.errorMessage,
        errorStatus: event.data.errorStatus,
        meta: meta,
        isFromAA: true,
        fiType: currentFlowType ? FlowType[currentFlowType] : undefined,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_${event.data.eventCode}`,
            }
          : {}),
      });
    }
    if (event.data.eventCode === 'AUTH_SCREEN_LOADED' && isCommonTenant()) {
      setAaAuthScreenLoaded(true);
    }
    if (
      (event.data.eventCode === 'IFRAME_SUCCESS' && getTenant() !== TenantType.SIB) ||
      (event.data.eventCode === 'AUTH_SCREEN_LOADED' && getTenant() === TenantType.SIB)
    ) {
      if (getTenant() === TenantType.YESBANK) {
        markIframeSuccess();
      }
      if (isPartial.current) {
        setPartialLoading(false);
      }
      setIsLoading(false);

      if (
        getTenant() === TenantType.BOB ||
        getTenant() === TenantType.BOB_V2
      ) {
        const frame: any = document.getElementById('aa-wrapper-iframe');
        if (frame && frame.contentWindow) {
          const linkedAccounts = (statusData.current?.accountDetails ?? [])
            .filter((account: any) => account.status === 'ACTIVE' || account.status === 'REVOKE_INITIATED' )
            .map((account: any) => ({
              type: 'BLACKLISTED_ACCOUNT',
              value: {
                fipId: account.fipId,
                maskedAccNumber: account.maskedAccountNumber,
                fiType: account.fiType,
              },
            }));
          frame.contentWindow.postMessage({
            PARENT_COMMAND: 'SECONDARY_IDENTIFIERS',
            secondaryIdentifiers: linkedAccounts,
          }, '*');
        }
      }

    } else if (
      event.data.eventCode === 'CONSENT_APPROVAL_INITIATED' &&
      isLoanJourney.current
    ) {
      setIsLoading(true);
      setAnalyticsStatus('PENDING');
      setIsAccountDetailsFetched(false);
      setLoaderType(LOADERTYPE.DATALOADER);
    } else if (event.data.eventCode === 'JOURNEY_COMPLETED') {
      if (
        getTenant() === TenantType.JFS &&
        getIsNewUser() &&
        !statusData.current?.approvedConsentsAvailable
      ) {
        if (nudgesEntryPending) {
          nudgesEntryPending.current = true;
        }
        deepLinkHandled.current = false;
      }

      if (getTenant() === TenantType.SIB) {
        // SIB shows its own account-linked result sheet inside the AA SDK.
        // Routing happens when the user taps "Okay" on that sheet, which the
        // SDK reports via the ACCOUNT_LINK_ACKNOWLEDGED event (handled below).
      } else if (
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.MAHINDRA
      ) {
        handleRoutingOnJourneyCompleted();
      } else {
        if (!isLoanJourney.current) {
          setAnalyticsStatus('PENDING');
          setIsAccountDetailsFetched(false);
          // Force an immediate status poll instead of waiting for the next
          // scheduled tick, so newly linked accounts' raw data (MF/Stocks
          // lists) refresh without needing a screen remount.
          setStatusPolling((prev: any) => prev + 1);
          setTimeout(() => {
            navigate('/home', { replace: true });
            gotoRoot();
          }, 500);
        }
      }
    } else if (event.data.eventCode === 'ACCOUNT_LINK_ACKNOWLEDGED') {
      if (getTenant() === TenantType.SIB) {
        updatedStatusDefaultConsentCreation();
        setAnalyticsStatus('PENDING');
        setIsAccountDetailsFetched(false);
        setSelectedFIP([]);
        gotoRoot();
      }
    } else if (event.data.eventCode === 'CONSENT_APPROVED_SUCCESS') {
      if (
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.JFS ||
        getTenant() === TenantType.SIB
      ) {
        isJourneyCompletedEventCame.current = true;
      }
    } else if (event.data.eventCode === 'JOURNEY_SKIPPED') {
      if (
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.MAHINDRA ||
        isCommonTenant()
      ) {
        setPreFetchedLoadingForAssets();
        handleRoutingOnJourneyCompleted();
      } else if (getTenant() === TenantType.SIB) {
        setAnalyticsStatus('PENDING');
        setIsAccountDetailsFetched(false);
        setSelectedFIP([]);
        gotoRoot();
      } else if (getTenant() === TenantType.JFS) {
        handleRoutingOnJourneyCompleted();
      } else if (getTenant() === TenantType.AU) {
        gotoRoot();
      } else {
        if (!statusData.current.approvedConsentsAvailable) {
          pushEventToParent({
            eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
          });
          backEventSent.current = true;
        }
        setIsLoading(false);
        goBackOnJourneyFailure();
      }
    } else if (event.data.eventCode === 'ADD_ANOTHER_BANK') {
      setIsLoading(false);
      setAAInternalStep(AASTEPS.FIP_SELECTION);
    } else if (event.data.eventCode === 'CONSENT_APPROVAL_INITIATION_FAILED') {
      setIsLoading(false);
      if (getTenant() === TenantType.JFS) {
        setShowToastAA(true);
        if (statusData.current.approvedConsentsAvailable) {
          setCurrentRoute(ROUTES.DASHBOARD);
          gotoRoot();
        } else {
          setCurrentRoute(ROUTES.JFS_WELCOME);
          navigate('/welcome');
        }
      } else if (getTenant() === TenantType.AU) {
        setShowAUErrorSheet(true);
        setCurrentRoute(ROUTES.DASHBOARD);
        gotoRoot();
      } else if (isCommonTenant()) {
        setAnalyticsStatus('PENDING');
        setPreFetchedLoadingForAssets();
        setCurrentRoute(ROUTES.DASHBOARD);
        gotoRoot();
      } else {
        setAAInternalStep(AASTEPS.CONSENT_FAILURE);
      }
    } else if (event.data.eventCode === 'NO_ACCOUNTS_FOUND') {
      setAccountDiscoveryStatus(AccountDiscoveryStatus.NoAccountsFound);
      if (isCommonTenant()) {
        setIsLoading(false);
        setAnalyticsStatus('PENDING');
        setPreFetchedLoadingForAssets();
        setCurrentRoute(ROUTES.DASHBOARD);
        gotoRoot();
      }
    } else if (
      event.data.eventCode === 'ACCOUNTS_DISCOVERED' ||
      event.data.eventCode === 'LINKED_ACCOUNT_FOUND'
    ) {
      setAccountDiscoveryStatus(AccountDiscoveryStatus.AccountsDiscovered);
      setShowHeader(true);
    }

    if (getTenant() === TenantType.MAHINDRA) {
      if (
        event.data.eventCode === 'CONSENT_APPROVED_SUCCESS' ||
        event.data.eventCode === 'CONSENT_APPROVED_FAILED' ||
        event.data.eventCode === 'CONSENT_REJECTED_SUCCESS' ||
        event.data.eventCode === 'CONSENT_REJECTED_FAILED'
      ) {
        setIsLoading(true);
        setTimeout(() => {
          setViewType(VIEWTYPE.BANKSUMMARYVIEW);
        }, 2000);
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).IGNOSIS_PFM_SNA_ENABLED = true;
    }
    window.addEventListener('message', handleSDKEvents);

    if (getTenant() === TenantType.JFS) {
      setIsHeaderVisible(false);
      setIsBottomNavbarVisible(false);
    }

    return () => {
      window.removeEventListener('message', handleSDKEvents);

      if (getTenant() === TenantType.JFS) {
        // setIsHeaderVisible(true);
        // setIsBottomNavbarVisible(true);
      }
    };
  }, []);

  const handleBackButton = () => {
    gotoRoot();
    navigate('/home', { replace: true });
  };

  const renderAAJourney = () => {
    if (isCommonTenant()) {
      return (
        <CommonTenantAA
          partialLoading={partialLoading}
          redirectionURL={redirectionURL}
          gotoRoot={gotoRoot}
          handleBackButton={handleBackButton}
          isInfoAvailable={isInfoAvailable}
          aaAuthScreenLoaded={aaAuthScreenLoaded}
        />
      );
    }
    switch (getTenant()) {
      case TenantType.PB:
        return (
          <PBAA
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            gotoRoot={gotoRoot}
            handleBackButton={handleBackButton}
            isInfoAvailable={isInfoAvailable}
          />
        );

      case TenantType.YESBANK:
        return (
          <YesBankAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );

      case TenantType.MAHINDRA:
        return (
          <MahindraAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );
      case TenantType.JFS:
        return (
          <JFSAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );

      case TenantType.AU:
        return (
          <AUAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );

      case TenantType.SIB:
        return (
          <SIBAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
            exitReasonSheetTrigger={exitReasonSheetTrigger}
            notesAcknowledged={sibNotesAcknowledged}
            onAcknowledgeNotes={onAcknowledgeSibNotes}
          />
        );

      default:
        return <></>;
    }
  };

  return <>{renderAAJourney()}</>;
};

export default AAWrapper;
